const { formatStaffSmsStamp } = require('./staffSmsThread');
const { groupByDate } = require('./dateGroups');

const SMS_INBOX_WINDOW_DAYS = 14;

function isSmsInboxWithinDays(date, days = SMS_INBOX_WINDOW_DAYS, now = new Date()) {
  const t = new Date(date).getTime();
  const n = new Date(now).getTime();
  if (!Number.isFinite(t) || !Number.isFinite(n)) return false;
  const windowMs = Number(days) * 24 * 60 * 60 * 1000;
  return t >= n - windowMs;
}

const SMS_INBOX_CACHE_KEY = 'pixie-sms-inbox-cache-v1';

function readSmsInboxCache(storage) {
  try {
    if (!storage || typeof storage.getItem !== 'function') return [];
    const raw = storage.getItem(SMS_INBOX_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const messages = Array.isArray(parsed)
      ? parsed
      : parsed && Array.isArray(parsed.messages)
        ? parsed.messages
        : [];
    return messages.filter((row) => row && row.id != null);
  } catch (err) {
    return [];
  }
}

function writeSmsInboxCache(messages, storage) {
  try {
    if (!storage || typeof storage.setItem !== 'function') return;
    storage.setItem(
      SMS_INBOX_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), messages: messages || [] }),
    );
  } catch (err) {
    /* quota / private mode */
  }
}

function keepRecentSmsInbox(
  inbox,
  days = SMS_INBOX_WINDOW_DAYS,
  now = new Date(),
) {
  return (Array.isArray(inbox) ? inbox : []).filter((row) =>
    isSmsInboxWithinDays(row && row.createdAt, days, now),
  );
}

function isSmsOptInAckMessage(row = {}) {
  if (row.direction === 'out') return false;
  if (String(row.templateKey || '').toLowerCase() === 'start') return true;
  const tokens = String(row.body || '')
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(/[^a-z]/gi, '').toLowerCase())
    .filter(Boolean);
  const word = tokens[0] || '';
  if (word === 'yes') return tokens.length === 1;
  return word === 'start' || word === 'unstop';
}

function searchAsksForOptInAck(query) {
  const tokens = String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter((part) => part && !part.startsWith('!'))
    .map((part) => part.replace(/[^a-z]/g, ''))
    .filter(Boolean);
  return tokens.some((word) => word === 'yes' || word === 'start' || word === 'unstop');
}

function smsInboxFilterTerms(query) {
  const parts = String(query || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const include = [];
  const exclude = [];
  for (const part of parts) {
    if (part.startsWith('!') && part.length > 1) {
      exclude.push(part.slice(1).toLowerCase());
    } else if (part !== '!') {
      include.push(part);
    }
  }
  return { include: include.join(' ').toLowerCase(), exclude };
}

function filterSmsInboxSearch(inbox, query) {
  const { include, exclude } = smsInboxFilterTerms(query);
  const list = (Array.isArray(inbox) ? inbox : []).filter((row) => {
    if (!isSmsOptInAckMessage(row)) return true;
    return searchAsksForOptInAck(include);
  });
  if (!include && !exclude.length) return list.slice();
  return list.filter((row) => {
    const hay = [
      row.body,
      row.name,
      row.property,
      row.header,
      row.phoneDigits,
      formatSmsPhone(row.phoneDigits),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (include && !hay.includes(include)) return false;
    return exclude.every((token) => !hay.includes(token));
  });
}

function formatSmsRelativeTime(date, now = new Date()) {
  const t = new Date(date).getTime();
  const n = new Date(now).getTime();
  if (!Number.isFinite(t) || !Number.isFinite(n)) return '';
  const sec = Math.max(0, Math.round((n - t) / 1000));
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

function formatSmsInboxStamp(date, expanded, now = new Date()) {
  if (expanded) return formatStaffSmsStamp(date);
  return formatSmsRelativeTime(date, now);
}

function formatSmsPhone(digits) {
  const raw = String(digits || '').replace(/\D/g, '');
  const ten = raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw;
  if (ten.length !== 10) return '';
  return `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}`;
}

function classifySmsParty(row = {}) {
  if (row.userId) {
    return {
      kind: 'staff',
      name: String(row.staffName || '').trim(),
      property: '',
    };
  }
  if (row.peopleId) {
    return {
      kind: 'tenant',
      name: String(row.tenantName || '').trim(),
      property: String(row.property || '').trim(),
    };
  }
  const externalName = String(row.externalName || '').trim();
  if (externalName || row.kind === 'external') {
    return { kind: 'external', name: externalName, property: '' };
  }
  return { kind: 'unknown', name: '', property: '' };
}

function smsInboxHeader(row = {}) {
  const phone = formatSmsPhone(row.phoneDigits);
  const kind = row.kind || 'unknown';
  const name = String(row.name || '').trim();
  const property = String(row.property || '').trim();
  if (kind === 'staff' && name) {
    return [phone, name].filter(Boolean).join(' · ');
  }
  if (kind === 'tenant' && name) {
    return [phone, name, property].filter(Boolean).join(' · ');
  }
  if (kind === 'external') {
    const label = name ? `${name} (external contact)` : '(external contact)';
    return phone ? `${phone} · ${label}` : label;
  }
  if (phone) return `${phone} · unknown`;
  return 'unknown';
}

function unknownSmsContactTarget(msg = {}) {
  if (!msg || msg.peopleId || msg.userId) return null;
  if (msg.kind && msg.kind !== 'unknown') return null;
  const header = String(msg.header || '');
  const match = header.match(/^(.*?)unknown$/);
  if (!match) return null;
  const phoneDigits =
    parseSmsSearchPhone(msg.phoneDigits) || parseSmsSearchPhone(header);
  if (!phoneDigits) return null;
  return { phoneDigits, prefix: match[1] };
}

function externalSmsContactTarget(msg = {}) {
  if (!msg || msg.peopleId || msg.userId) return null;
  if (msg.kind === 'unknown') return null;
  const header = String(msg.header || '');
  const named = header.match(
    /^(\d{3}-\d{3}-\d{4}) · (.+) \(external contact\)$/,
  );
  const bare = header.match(/^(\d{3}-\d{3}-\d{4}) · \(external contact\)$/);
  const fromHeader = named || bare;
  if (msg.kind && msg.kind !== 'external') return null;
  if (msg.kind !== 'external' && !fromHeader) return null;
  const phoneDigits =
    parseSmsSearchPhone(msg.phoneDigits) ||
    (fromHeader ? fromHeader[1].replace(/-/g, '') : '') ||
    parseSmsSearchPhone(header);
  if (!phoneDigits) return null;
  const name = String(
    msg.name || msg.externalName || (named ? named[2] : '') || '',
  ).trim();
  return { phoneDigits, name };
}

function externalContactEditError(reason) {
  const code = String(reason || '');
  if (code === 'known_number') {
    return 'That number belongs to a tenant or staff member.';
  }
  if (code === 'phone_taken') return 'That number is already another contact.';
  if (code === 'phone') return 'Enter a 10-digit phone number.';
  if (code === 'name') return 'Enter a name.';
  if (code === 'missing') return 'That contact is no longer saved.';
  return code || 'Could not save contact';
}

function applyExternalContactToInbox(inbox, { phoneDigits, name } = {}) {
  const digits = parseSmsSearchPhone(phoneDigits);
  const label = String(name || '').trim();
  if (!digits || !label) return Array.isArray(inbox) ? inbox.slice() : [];
  return (Array.isArray(inbox) ? inbox : []).map((row) => {
    const rowDigits = parseSmsSearchPhone(row && row.phoneDigits);
    if (rowDigits !== digits || (row && (row.peopleId || row.userId))) return row;
    return {
      ...row,
      kind: 'external',
      name: label,
      externalName: label,
      header: smsInboxHeader({
        kind: 'external',
        name: label,
        phoneDigits: digits,
      }),
    };
  });
}

function applyExternalContactEditToInbox(
  inbox,
  { fromPhoneDigits, phoneDigits, name } = {},
) {
  const from = parseSmsSearchPhone(fromPhoneDigits);
  const digits = parseSmsSearchPhone(phoneDigits);
  const label = String(name || '').trim();
  if (!from || !digits || !label) return Array.isArray(inbox) ? inbox.slice() : [];
  return (Array.isArray(inbox) ? inbox : []).map((row) => {
    const rowDigits = parseSmsSearchPhone(row && row.phoneDigits);
    if (rowDigits !== from || (row && (row.peopleId || row.userId))) return row;
    return {
      ...row,
      kind: 'external',
      phoneDigits: digits,
      name: label,
      externalName: label,
      header: smsInboxHeader({
        kind: 'external',
        name: label,
        phoneDigits: digits,
      }),
    };
  });
}

function smsSelectionIsText(selection) {
  if (!selection || selection.isCollapsed) return false;
  return String(selection.toString() || '').trim().length > 0;
}

function parseSmsSearchPhone(query) {
  const raw = String(query || '').replace(/\D/g, '');
  const ten = raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw;
  return ten.length === 10 ? ten : null;
}

function smsRecipientSearchStatus({
  query,
  searching,
  hits,
  failed,
  hasNewNumber,
} = {}) {
  const q = String(query || '').trim();
  if (!q || searching) return '';
  if (failed) return 'System not responding';
  const count = Array.isArray(hits) ? hits.length : 0;
  if (!count && !hasNewNumber) return 'No matches.';
  return '';
}

function smsNewNumberOption(query, hits) {
  const digits = parseSmsSearchPhone(query);
  if (!digits) return null;
  const taken = (Array.isArray(hits) ? hits : []).some(
    (hit) => parseSmsSearchPhone(hit && hit.phone) === digits,
  );
  if (taken) return null;
  return {
    kind: 'external',
    phoneDigits: digits,
    phone: formatSmsPhone(digits),
  };
}

function filterSmsRecipients(list, query) {
  const q = String(query || '').trim().toLowerCase();
  const rows = list || [];
  if (!q) return rows.slice();
  return rows.filter((r) => {
    const hay = [r.name, r.property, r.phone, r.kind]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

function smsInboxPartyKey(msg = {}) {
  if (msg.userId) return `user:${Number(msg.userId)}`;
  if (msg.peopleId) return `people:${Number(msg.peopleId)}`;
  const digits = String(msg.phoneDigits || '').replace(/\D/g, '');
  const ten =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length === 10) return `phone:${ten}`;
  if (msg.id != null) return `id:${Number(msg.id)}`;
  return '';
}

function filterSmsInboxByParty(inbox, partyKey) {
  const list = Array.isArray(inbox) ? inbox : [];
  if (!partyKey) return list.slice();
  return list.filter((row) => smsInboxPartyKey(row) === partyKey);
}

function nextSmsInboxPartyFilter(currentKey, msg) {
  const key = smsInboxPartyKey(msg);
  if (!key) return currentKey || null;
  return currentKey === key ? null : key;
}

function composeTargetFromPartyFilter(inbox, partyKey, label) {
  const key = String(partyKey || '').trim();
  if (!key) return null;
  const msg = filterSmsInboxByParty(inbox, key).find(
    (row) => row && (row.peopleId || row.userId || row.phoneDigits),
  );
  if (!msg) return null;
  return {
    peopleId: msg.peopleId || null,
    userId: msg.userId || null,
    phoneDigits: String(msg.phoneDigits || ''),
    externalName:
      msg.kind === 'external' ? String(msg.name || '').trim() : '',
    label: String(label || msg.header || '').trim(),
    inReplyToId: null,
  };
}

function smsInboxIsEmbedded(routePath) {
  return String(routePath || '') !== '/msms';
}

function smsComposeCtrlEnter(event) {
  if (!event || event.isComposing) return false;
  if (!event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) {
    return false;
  }
  return event.key === 'Enter' || event.code === 'NumpadEnter';
}

function smsInboxTypingTarget(el) {
  if (!el || !el.tagName) return false;
  const tag = String(el.tagName).toLowerCase();
  if (tag === 'textarea' || tag === 'input' || tag === 'select') return true;
  return !!el.isContentEditable;
}

function smsInboxHotkey(event, { pointerInside = false, blocked = false } = {}) {
  if (!event || event.repeat || event.isComposing) return null;
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (!pointerInside || blocked) return null;
  if (smsInboxTypingTarget(event.target)) return null;
  const key = String(event.key || '').toLowerCase();
  if (key === 'n') return 'new';
  if (key === 'r') return 'reply';
  return null;
}

function smsInboxLastSender(list) {
  const rows = (Array.isArray(list) ? list : []).filter(
    (row) => row && row.id != null,
  );
  const inbound = rows.filter((row) => row.direction !== 'out');
  const pool = inbound.length ? inbound : rows;
  pool.sort((a, b) => {
    const diff = smsInboxMessageTime(b) - smsInboxMessageTime(a);
    if (diff) return diff;
    return Number(b.id) - Number(a.id);
  });
  return pool[0] || null;
}

function smsInboxEscapeAction({
  showSearch = false,
  hasCompose = false,
  hasListFilter = false,
  hasPartyFilter = false,
} = {}) {
  if (showSearch) return 'closeSearch';
  if (hasCompose) return 'closeCompose';
  if (hasListFilter || hasPartyFilter) return 'clearFilters';
  return null;
}

function parseSmsInboxSocketPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.filter((row) => row && row.id != null);
  }
  if (Array.isArray(payload.messages)) {
    return payload.messages.filter((row) => row && row.id != null);
  }
  if (payload.id != null) return [payload];
  return [];
}

function smsInboxMessageTime(row) {
  const t = new Date(row && (row.createdAt || row.createdOn)).getTime();
  return Number.isFinite(t) ? t : 0;
}

function groupSmsInboxByDate(list, now = new Date()) {
  const rows = (Array.isArray(list) ? list : []).slice().sort((a, b) => {
    const diff = smsInboxMessageTime(a) - smsInboxMessageTime(b);
    if (diff) return diff;
    return Number(a && a.id) - Number(b && b.id);
  });
  return groupByDate(
    rows,
    now,
    (row) => row && (row.createdAt || row.createdOn),
  )
    .slice()
    .reverse();
}

function mergeSmsInboxMessages(inbox, incoming) {
  const byId = new Map();
  for (const row of Array.isArray(inbox) ? inbox : []) {
    if (row && row.id != null) byId.set(Number(row.id), row);
  }
  for (const row of parseSmsInboxSocketPayload(incoming)) {
    const id = Number(row.id);
    byId.set(id, row);
  }
  return [...byId.values()].sort((a, b) => Number(b.id) - Number(a.id));
}

function smsCanRetryPending(msg = {}) {
  if (!msg || msg.optimistic) return false;
  const dot = smsInboxSendDot(msg);
  if (dot !== 'pending' && dot !== 'failed') return false;
  const id = Number(msg.id);
  return Number.isFinite(id) && id > 0;
}

function smsInboxSendDot(msg = {}) {
  if (msg.direction !== 'out') return null;
  if (
    msg.sendScheduled ||
    String(msg.status || '').toLowerCase() === 'scheduled'
  ) {
    return null;
  }
  if (msg.sendFailed) return 'failed';
  if (
    msg.sendPending ||
    msg.pendingOptIn ||
    ['pending', 'pending_opt_in'].includes(String(msg.status || '').toLowerCase())
  ) {
    return 'pending';
  }
  return 'sent';
}

function optimisticOutboundMessage({
  body,
  composeTarget = {},
  media = [],
  now = Date.now(),
  scheduledFor = null,
} = {}) {
  const phoneDigits = parseSmsSearchPhone(composeTarget.phoneDigits) || '';
  const externalName = String(composeTarget.externalName || '').trim();
  const when = scheduledFor || null;
  const kind = composeTarget.userId
    ? 'staff'
    : composeTarget.peopleId
      ? 'tenant'
      : phoneDigits
        ? 'external'
        : 'unknown';
  const name = kind === 'external' ? externalName : '';
  return {
    id: Math.abs(Number(now) || Date.now()),
    direction: 'out',
    body: String(body || ''),
    sendPending: !when,
    sendScheduled: !!when,
    sendFailed: false,
    optimistic: true,
    status: when ? 'scheduled' : 'pending',
    scheduledFor: when,
    header:
      composeTarget.label ||
      smsInboxHeader({
        kind,
        name,
        phoneDigits,
      }),
    peopleId: composeTarget.peopleId || null,
    userId: composeTarget.userId || null,
    createdAt: new Date(now).toISOString(),
    inReplyTo: '',
    phoneDigits,
    kind,
    name,
    property: '',
    templateKey: composeTarget.inReplyToId ? 'sms_reply' : 'sms_compose',
    media: Array.isArray(media) ? media : [],
  };
}

function dropInboxMessageById(inbox, id) {
  const drop = Number(id);
  return (Array.isArray(inbox) ? inbox : []).filter(
    (row) => Number(row && row.id) !== drop,
  );
}

const SMS_REFRESH_FAST_MS = 3000;
const SMS_REFRESH_MAX_MS = 60000;

/** Short polls after activity, then double until the cap. */
function smsRefreshDelay(step) {
  const n = Math.max(0, Math.floor(Number(step) || 0));
  const delay = SMS_REFRESH_FAST_MS * 2 ** n;
  return Math.min(SMS_REFRESH_MAX_MS, delay);
}

function smsInboxActivityKey(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) =>
      [
        row && row.id,
        row && row.status,
        row && row.sendFailed ? 1 : 0,
        row && row.sendPending ? 1 : 0,
      ].join(':'),
    )
    .join('|');
}

function smsInboxHasLiveSend(rows) {
  return (Array.isArray(rows) ? rows : []).some((row) => {
    if (!row || row.direction === 'in') return false;
    const status = String(row.status || '').toLowerCase();
    return (
      row.sendPending ||
      row.pendingOptIn ||
      status === 'pending' ||
      status === 'pending_opt_in' ||
      status === 'queued' ||
      status === 'sending' ||
      status === 'accepted'
    );
  });
}

function nextSmsRefreshStep(step, beforeKey, afterKey, rows) {
  if (smsInboxHasLiveSend(rows)) return 0;
  if (beforeKey !== afterKey) return 0;
  return Math.max(0, Math.floor(Number(step) || 0)) + 1;
}

function composeRestoreFromFailed(msg = {}) {
  if (!msg || msg.direction !== 'out' || !msg.sendFailed) return null;
  return {
    body: String(msg.body || ''),
    composeTarget: {
      peopleId: msg.peopleId || null,
      userId: msg.userId || null,
      phoneDigits: msg.phoneDigits || '',
      externalName: msg.kind === 'external' ? String(msg.name || '').trim() : '',
      label: msg.header || '',
      inReplyToId: null,
    },
  };
}

function smsInboxIsReply(msg = {}) {
  if (String(msg && msg.inReplyTo ? msg.inReplyTo : '').trim()) return true;
  return String(msg && msg.templateKey ? msg.templateKey : '') === 'sms_reply';
}

function smsBodyIsClipped(el) {
  if (!el || el.scrollHeight == null || el.clientHeight == null) return false;
  return el.scrollHeight > el.clientHeight + 1;
}

/** Clipped text expands first. A second click, or a message that already fits, opens reply. */
function smsSelectAction({ clipped, alreadyExpanded } = {}) {
  if (alreadyExpanded) return 'reply';
  if (clipped) return 'expand';
  return 'reply';
}

function inReplyToPreview(body, max = 80) {
  const t = String(body || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return '';
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(1, max - 1))}…`;
}

module.exports = {
  SMS_INBOX_WINDOW_DAYS,
  SMS_INBOX_CACHE_KEY,
  readSmsInboxCache,
  writeSmsInboxCache,
  isSmsInboxWithinDays,
  keepRecentSmsInbox,
  filterSmsInboxSearch,
  formatSmsRelativeTime,
  formatSmsInboxStamp,
  formatSmsPhone,
  classifySmsParty,
  smsInboxHeader,
  unknownSmsContactTarget,
  externalSmsContactTarget,
  externalContactEditError,
  applyExternalContactToInbox,
  applyExternalContactEditToInbox,
  smsSelectionIsText,
  filterSmsRecipients,
  parseSmsInboxSocketPayload,
  mergeSmsInboxMessages,
  groupSmsInboxByDate,
  smsInboxPartyKey,
  filterSmsInboxByParty,
  nextSmsInboxPartyFilter,
  composeTargetFromPartyFilter,
  smsComposeCtrlEnter,
  smsInboxEscapeAction,
  smsInboxHotkey,
  smsInboxTypingTarget,
  smsInboxLastSender,
  smsInboxIsEmbedded,
  inReplyToPreview,
  smsBodyIsClipped,
  smsSelectAction,
  smsInboxIsReply,
  smsInboxSendDot,
  smsCanRetryPending,
  composeRestoreFromFailed,
  optimisticOutboundMessage,
  dropInboxMessageById,
  SMS_REFRESH_FAST_MS,
  SMS_REFRESH_MAX_MS,
  smsRefreshDelay,
  smsInboxActivityKey,
  smsInboxHasLiveSend,
  nextSmsRefreshStep,
  parseSmsSearchPhone,
  smsNewNumberOption,
  smsRecipientSearchStatus,
  smsPendingOptInPopup,
};

function smsPendingOptInPopup({ peopleId, userId, inbox } = {}) {
  if (userId || !peopleId) return null;
  const held = (Array.isArray(inbox) ? inbox : []).some(
    (row) =>
      row &&
      row.pendingOptIn &&
      Number(row.peopleId) === Number(peopleId),
  );
  if (!held) return null;
  return 'This tenant has not opted in yet. Wait for YES or START before sending more messages.';
}
